import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { routes } from '@/app/routes'
import { env } from '@/config/env'
import { cn, mediaUrl } from '@/lib/utils'
import { DsAlert, DsAvatar, DsBadge, DsButton, DsCard, DsCardHeader, DsCheckbox, DsChoiceCard, DsDrawer, DsEmptyState, DsFetchErrorState, DsIcons, DsInput, DsListGroup, DsListItem, DsMoney, DsMoneyInput, dsMoneyText, DsReceipt, DsSkeleton, DsSkeletonListRow, DsText, type DsReceiptRow } from '@/ds'
import { leaseApi } from '@/features/lease/lease.api'
import { tenantName } from '@/features/lease/lease-utils'
import { rentDisplayStatus, rentMonth, rentStatusLabel, rentStatusTone } from '@/features/lease/rent-ledger-utils'
import { leaseKeys } from '@/features/lease/useLeases'
import { CheckoutStatus } from '@/features/payments/CheckoutStatus'
import { amountHelper, groupCard, isOnlineGroup, leaseDueAmount, openPeriods, periodCoverage, quickAmounts } from '@/features/payments/pay-rent-utils'
import { payRentStrings as s } from '@/features/payments/pay-rent-strings'
import { usePayRent, type PayRentVm } from '@/features/payments/usePayRent'
import { LeaseCard, LeaseNotPayable, PaymentSuccess } from '@/pages/payments/parts'

// A rent payment is in its lease's currency (the backend copies it from the property).
const moneyIn = (currency?: string) => (n: number) => dsMoneyText(n, { currency })

export interface PaymentDrawerProps {
  leaseId?: string
  amount?: number
  onClose: () => void
}

// Record a payment / Pay rent as a drawer from 1024 px: unpaid rent as a table (ticking a month sets the amount that clears it and
// every older one) on the left, the payment form on the right.
export function PaymentDrawer({ leaseId, amount, onClose }: PaymentDrawerProps) {
  const navigate = useNavigate()
  const vm = usePayRent(leaseId, amount && amount > 0 ? amount : null)
  const title = vm.isLandlord ? s.titleLandlord : s.titleTenant
  const done = () => {
    onClose()
    if (!vm.isLandlord && vm.result) navigate(routes.lease(vm.result.lease.id))
  }

  return (
    <DsDrawer open onClose={onClose} size="xl" title={title} description={vm.stage === 'form' ? vm.lease?.property?.title : undefined} closeLabel={s.cancel}>
      {vm.stage === 'success' && vm.result ? (
        <PaymentSuccess vm={vm} onDone={done} page={false} />
      ) : vm.stage === 'lease' ? (
        <LeasePicker vm={vm} />
      ) : vm.leaseLoading || !vm.lease ? (
        vm.leaseError ? <DsFetchErrorState error={vm.leaseError} onRetry={() => void vm.refetchLease()} /> : <DsSkeleton height={240} radius={16} />
      ) : vm.blocked ? (
        <LeaseNotPayable lease={vm.lease} />
      ) : (
        <Form vm={vm} canChangeLease={!leaseId && vm.isLandlord} />
      )}
      <CheckoutStatus phase={vm.checkout.phase} error={vm.checkout.error} countdown={vm.checkout.countdown} copy={{ successTitle: s.successTitleTenant, successDescription: s.successSent }} onDone={vm.checkout.reset} onClose={vm.checkout.reset} onRetry={() => void vm.submit()} />
    </DsDrawer>
  )
}

function LeasePicker({ vm }: { vm: PayRentVm }) {
  if (vm.leasesLoading) return <DsListGroup>{Array.from({ length: 5 }).map((_, i) => <DsSkeletonListRow key={i} />)}</DsListGroup>
  if (vm.leasesError) return <DsFetchErrorState error={vm.leasesError} onRetry={() => void vm.refetchLeases()} />
  if (vm.leases.length === 0) return <DsEmptyState icon={DsIcons.lease} title={s.noLeases} compact />
  return (
    <div className="flex flex-col gap-3">
      <DsText variant="bodyStrong">{s.pickLease}</DsText>
      <DsListGroup>
        {vm.leases.map((l) => {
          const due = leaseDueAmount(l)
          return (
            <DsListItem
              key={l.id}
              leading={<DsAvatar name={tenantName(l)} imageUrl={mediaUrl(l.tenant?.avatar, env.mediaHost)} />}
              title={tenantName(l)}
              subtitle={l.property?.title}
              onClick={() => vm.pickLease(l)}
              trailing={due > 0 ? <DsMoney amount={due} currency={l.currency} size="sm" tone="warning" bottomLabel={s.due} align="end" /> : <DsBadge label={s.upToDate} tone="success" size="xs" icon={DsIcons.check} />}
              showChevron
            />
          )
        })}
      </DsListGroup>
    </div>
  )
}

function Periods({ vm }: { vm: PayRentVm }) {
  const leaseId = vm.lease!.id
  const money = moneyIn(vm.lease!.currency)
  const rents = useQuery({ queryKey: leaseKeys.rents(leaseId), queryFn: () => leaseApi.rents(leaseId), retry: 1 })
  const periods = openPeriods(rents.data?.items ?? [])
  const coverage = periodCoverage(periods, vm.amount)
  // Ticking a month pays up to it; unticking the last ticked one steps back to the month before.
  const toggle = (i: number) => {
    const lastFull = coverage.lastIndexOf('full')
    vm.setAmount(coverage[i] === 'full' && i === lastFull ? (i > 0 ? periods[i - 1].cumulative : null) : periods[i].cumulative)
  }

  return (
    <DsCard padding="none">
      <DsCardHeader title={s.periodsTitle} description={periods.length > 1 ? s.periodsHint : undefined} />
      {rents.isPending ? (
        <div className="px-4 pb-4"><DsSkeleton height={120} radius={12} /></div>
      ) : rents.isError ? (
        <div className="px-4 pb-4"><DsFetchErrorState error={rents.error} onRetry={() => void rents.refetch()} compact /></div>
      ) : periods.length === 0 ? (
        <DsText variant="small" muted className="px-4 pb-4">{s.periodsNone}</DsText>
      ) : (
        <table className="w-full text-left text-small">
          <caption className="sr-only">{s.periodsCaption}</caption>
          <thead className="border-y bg-muted/60 text-caption uppercase text-muted-foreground">
            <tr>
              <th scope="col" className="w-10 px-4 py-2"><span className="sr-only">{s.payUpTo('')}</span></th>
              <th scope="col" className="px-2 py-2 font-semibold">{s.colPeriod}</th>
              <th scope="col" className="px-4 py-2 text-right font-semibold">{s.colAmount}</th>
              <th scope="col" className="px-4 py-2 font-semibold">{s.colStatus}</th>
            </tr>
          </thead>
          <tbody className="[&>tr+tr]:border-t">
            {periods.map((p, i) => {
              const st = rentDisplayStatus(p.rent)
              const month = rentMonth(p.rent)
              return (
                <tr key={p.rent.id} className={cn('cursor-pointer transition-colors motion-fast hover:bg-muted/50', coverage[i] !== 'none' && 'bg-accent/40')} onClick={() => toggle(i)}>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <span aria-label={s.payUpTo(month)} className="block">
                      <DsCheckbox checked={coverage[i] === 'full'} onChange={() => toggle(i)} />
                    </span>
                  </td>
                  <td className="px-2 py-2 text-body-strong text-foreground">
                    {month}
                    {coverage[i] === 'part' && <DsBadge label={s.partly} tone="info" size="xs" className="ml-2" />}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{money(p.balance)}</td>
                  <td className="px-4 py-2"><DsBadge label={rentStatusLabel(st)} tone={rentStatusTone[st]} size="xs" leadingDot /></td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </DsCard>
  )
}

function Form({ vm, canChangeLease }: { vm: PayRentVm; canChangeLease: boolean }) {
  const lease = vm.lease!
  const money = moneyIn(lease.currency)
  const amount = vm.amount ?? 0
  const online = !vm.isLandlord && isOnlineGroup(vm.group)
  const fb = vm.fee?.feeBreakdown
  const groupLabel = vm.group === 'offline' || vm.isLandlord ? s.methodCash : vm.groups.find((g) => g.key === vm.group)?.label ?? vm.group
  const rows: DsReceiptRow[] = [
    ...(vm.isLandlord ? [{ label: s.paidBy, value: tenantName(lease) }] : []),
    { label: s.rent, value: money(amount) },
    ...(online && fb?.rentGstAmount ? [{ label: s.gstOnRent, value: money(fb.rentGstAmount) }] : []),
    ...(online && fb ? [{ label: s.gatewayFee, value: money(fb.tenantRazorpayAmount ?? 0) }, { label: s.platformFee, value: money(fb.platformFeeTotal ?? 0) }] : []),
    { label: s.method, value: groupLabel },
  ]
  const payable = online && fb ? fb.tenantPayable ?? amount : amount

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_300px] items-start gap-6">
      <div className="flex min-w-0 flex-col gap-4">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1"><LeaseCard vm={vm} lease={lease} /></div>
          {canChangeLease && <DsButton variant="ghost" size="sm" label={s.changeLease} onClick={() => vm.setStage('lease')} />}
        </div>
        <Periods vm={vm} />
        <DsInput label={s.noteLabel} hintText={s.noteHint} value={vm.note} onChange={vm.setNote} maxLines={2} />
      </div>
      <aside className="sticky top-0 flex flex-col gap-4">
        <DsMoneyInput label={s.amountLabel} value={vm.amount} onChange={vm.setAmount} currency={lease.currency} required quickAmounts={quickAmounts(lease)} helper={amountHelper(vm.amount, vm.due, money)} />
        {!vm.isLandlord && vm.groups.length > 0 && (
          <div className="flex flex-col gap-2">
            <DsText variant="label">{s.methodLabel}</DsText>
            {/* Same as PaymentAddPage: say why the online options are missing. */}
            {!vm.paymentsAllowed && <DsAlert tone="info" description={s.onlineNotInCountry} />}
            {vm.groups.map((g) => {
              const a = vm.availability[g.key]
              if (a && !a.visible) return null
              const card = groupCard(g)
              return (
                <div key={g.key} className={a && !a.enabled ? 'opacity-50' : undefined}>
                  <DsChoiceCard value={g.key} groupValue={vm.group} onChange={() => vm.selectGroup(g)} title={card.title} description={card.description} icon={card.icon} trailing={g.feePercent ? <DsText variant="caption" muted>{g.feePercent}%</DsText> : undefined} />
                </div>
              )
            })}
          </div>
        )}
        <DsReceipt title={s.summaryTitle} rows={rows} total={{ label: online ? s.youPay : s.total, value: vm.feeLoading ? <DsSkeleton width={70} height={18} /> : money(payable), strong: true }} note={!vm.isLandlord && !online ? s.cashPendingNote : undefined} />
        {vm.error && <DsAlert tone="danger" description={vm.error} />}
        <DsButton label={vm.isLandlord ? s.submitRecord(money(amount)) : s.submitPay(money(payable))} onClick={vm.valid ? () => void vm.submit() : undefined} disabled={!vm.valid} loading={vm.submitting || vm.feeLoading} fullWidth />
      </aside>
    </div>
  )
}
